// src/Pages/NewReservation/StepOne/algorithm/filters/maxGroupsFilter.js
// Import the dedicated getMealType function instead of relying on the openinghours version
// This ensures consistency in meal type determination

/**
 * Determines the meal type (breakfast, lunch, dinner) based on the time.
 * @param {string} time - Time in HH:MM format
 * @returns {string|null} - Meal type or null if outside meal times
 */
function getMealType(time) {
	const hour = parseInt(time.split(':')[0], 10);
	
	if (hour >= 4 && hour < 11) return 'breakfast';
	if (hour >= 11 && hour < 16) return 'lunch';
	if (hour >= 16 && hour < 23) return 'dinner';
	
	return null;
  }
  
  // --- Add console logs for debugging ---
  // Set DEBUG_MAX_GROUPS to true in your development environment to enable detailed logs
  const DEBUG_MAX_GROUPS = true; // Set to true to enable detailed logs for this filter
  
  /**
   * Helper function for conditional logging.
   * @param {string} message - The log message.
   * @param  {...any} args - Additional arguments to log.
   */
  function logDebug(message, ...args) {
	if (DEBUG_MAX_GROUPS) {
	  // Using console.debug might allow filtering in browser dev tools,
	  // but console.log ensures visibility.
	  console.log(`[MaxGroupsFilter] ${message}`, ...args);
	}
  }
  // --- End log setup ---
  
  /**
   * Extracts a number value from various data formats (e.g., MongoDB NumberInt).
   * Handles null, undefined, object with $numberInt, number, and string representations.
   * Returns null if the value is not a valid integer representation.
   * @param {*} value - Value from data object.
   * @returns {number|null} - Parsed integer or null if invalid.
   */
  function extractNumber(value) {
	if (value === null || value === undefined) {
	  return null;
	}
  
	// Handle MongoDB NumberInt format: { "$numberInt": "1" }
	if (typeof value === 'object' && value !== null && '$numberInt' in value) {
	  const parsed = parseInt(value.$numberInt, 10);
	  return isNaN(parsed) ? null : parsed;
	}
  
	// Handle regular number type
	if (typeof value === 'number') {
	  // Ensure it's an integer, not float/Infinity/NaN
	  return Number.isInteger(value) ? value : null;
	}
  
	// Handle string number: "1"
	if (typeof value === 'string') {
	  const parsed = parseInt(value, 10);
	  // Ensure the string was fully parsed as a number and is finite
	  return isNaN(parsed) ? null : parsed;
	}
  
	// Not a recognized number format
	return null;
  }
  
  /**
   * Filters available timeblocks based on maximum group size limits for each meal period.
   * It checks if adding a new reservation of 'guests' count would exceed the configured
   * maximum number of groups allowed for various size thresholds within the specific meal period.
   *
   * @param {Object} restaurantData - The main restaurant data object, expected to contain keys like 'max-groups-breakfast', 'max-groups-lunch', etc.
   * @param {string} dateStr - The date string in "YYYY-MM-DD" format.
   * @param {Object} timeblocks - An object of available timeblocks { "HH:MM": timeData } that have passed previous filters.
   * @param {Array} reservations - An array of existing reservation objects for potentially multiple dates.
   * @param {number|string} guests - The number of guests for the new reservation being considered.
   * @returns {Object} - A new object containing only the timeblocks that satisfy the max group limits.
   */
  function filterTimeblocksByMaxGroups(restaurantData, dateStr, timeblocks, reservations, guests) {
	logDebug(`--- Running MaxGroupsFilter for Date: ${dateStr}, Guests: ${guests} ---`);
	// Log the top-level keys related to max-groups to see what's actually present
	const groupKeys = Object.keys(restaurantData).filter(k => k.startsWith('max-groups-'));
	logDebug("Received restaurantData max-group keys:", groupKeys.length > 0 ? groupKeys : 'None found');
  
	const filteredBlocks = {}; // Initialize the object to store timeblocks that pass the filter
	const guestsNumber = parseInt(guests, 10); // Ensure guest count is a number
  
	// Validate guest input
	if (isNaN(guestsNumber) || guestsNumber <= 0) {
	  logDebug(`Invalid guest count (${guests}). Returning all ${Object.keys(timeblocks).length} input blocks unfiltered.`);
	  return timeblocks; // Return original blocks if guest count is invalid
	}
  
	// Pre-filter reservations to only those on the specific date for efficiency
	const reservationsOnDate = reservations.filter(r => r.date === dateStr);
	logDebug(`Relevant reservations on ${dateStr}: ${reservationsOnDate.length}`);
	logDebug(`Input timeblocks count: ${Object.keys(timeblocks).length}`);
  
	// Group reservations by meal type using our consistent getMealType function
	const reservationsByMealType = {};
	reservationsOnDate.forEach(r => {
	  const mealType = getMealType(r.time);
	  if (!mealType) return; // Skip if no meal type determined
	  
	  if (!reservationsByMealType[mealType]) {
		reservationsByMealType[mealType] = [];
	  }
	  reservationsByMealType[mealType].push(r);
	});
	
	logDebug('Reservations grouped by meal type:', reservationsByMealType);
  
	// Iterate through each timeblock that needs checking
	for (const [time, timeData] of Object.entries(timeblocks)) {
	  const mealType = getMealType(time); // Determine meal period (e.g., 'breakfast') using our consistent function
	  logDebug(`\nChecking Time: ${time} -> Determined Meal: ${mealType}`);
  
	  // If the time doesn't fall into a recognized meal period, the filter doesn't apply
	  if (!mealType) {
		logDebug(`  Time ${time} has no associated meal type. Keeping block.`);
		filteredBlocks[time] = timeData;
		continue;
	  }
  
	  // Construct the key for the settings specific to this meal (e.g., 'max-groups-breakfast')
	  const maxGroupSettingsKey = `max-groups-${mealType}`;
	  // Retrieve the settings object for this meal from the restaurant data
	  const maxGroupSettings = restaurantData[maxGroupSettingsKey];
  
	  logDebug(`  Looking for settings using key: "${maxGroupSettingsKey}"`);
	  // If no settings object exists for this meal type, the filter doesn't apply to this timeblock
	  if (!maxGroupSettings || typeof maxGroupSettings !== 'object') {
		logDebug(`  No settings found for key "${maxGroupSettingsKey}". Filter doesn't apply. Keeping block.`);
		filteredBlocks[time] = timeData;
		continue; // Move to the next timeblock
	  }
	  // Log the actual settings found for this meal to verify correctness
	  logDebug(`  Found settings for ${mealType}:`, JSON.stringify(maxGroupSettings));
  
	  // --- Settings exist, now apply the filter logic ---
	  let isTimeAllowed = true; // Assume the timeblock is allowed until a limit is violated
  
	  // Extract and sort the numeric group size limits defined in the settings (e.g., "6", "7" -> [6, 7])
	  const limitSizes = Object.keys(maxGroupSettings)
							 .map(key => parseInt(key, 10)) // Convert keys to numbers
							 .filter(num => !isNaN(num) && num > 0) // Keep only positive integers
							 .sort((a, b) => a - b); // Sort numerically (e.g., check 6 before 7)
	  logDebug(`  Defined numeric limit sizes to check for ${mealType}: [${limitSizes.join(', ')}]`);
  
	  // Check against each defined limit size threshold
	  for (const limitSize of limitSizes) {
		const limitSizeStr = String(limitSize); // Original string key (e.g., "6")
		logDebug(`    Checking Limit Condition: Groups of size >= ${limitSize}`);
  
		// Does the new booking we're considering (guestsNumber) meet or exceed this limit threshold?
		// Example: If limitSize is 6, and guestsNumber is 7, this limit applies. If guestsNumber is 5, it doesn't.
		if (guestsNumber >= limitSize) {
		  logDebug(`      Applicable: Booking guests (${guestsNumber}) >= Limit threshold (${limitSize})`);
  
		  // Extract the maximum number of groups allowed for this threshold
		  const maxAllowedCount = extractNumber(maxGroupSettings[limitSizeStr]);
		  logDebug(`      Max groups allowed for >=${limitSize} is: ${maxAllowedCount} (raw setting: ${JSON.stringify(maxGroupSettings[limitSizeStr])})`);
  
		  // If the setting for this limit size is invalid (null or negative), treat it as 'no limit'
		  if (maxAllowedCount === null || maxAllowedCount < 0) {
			logDebug(`      Skipping check: Invalid or non-positive max count defined for size ${limitSize}.`);
			continue; // Skip to the next limitSize
		  }
  
		  // Get reservations for this meal type - using only reservations from the same meal type
		  const reservationsForMealType = reservationsByMealType[mealType] || [];
		  
		  // Count how many existing reservations for this meal type meet or exceed this limit size
		  const existingMatchingGroupsCount = reservationsForMealType.filter(reservation => {
			const reservationGuests = parseInt(reservation.guests, 10);
			return !isNaN(reservationGuests) && reservationGuests >= limitSize;
		  }).length;
		  
		  logDebug(`      Count of existing groups >=${limitSize} found in ${mealType}: ${existingMatchingGroupsCount}`);
  
		  // THE CORE LOGIC: Check if adding the new group would exceed the max allowed count
		  if (existingMatchingGroupsCount + 1 > maxAllowedCount) {
			logDebug(`      *** VIOLATION DETECTED *** for limit >=${limitSize}.`);
			logDebug(`      (Existing Groups: ${existingMatchingGroupsCount} + New Group: 1 = ${existingMatchingGroupsCount + 1}, which is > Max Allowed: ${maxAllowedCount})`);
			logDebug(`      Blocking time ${time} due to this violation.`);
			isTimeAllowed = false; // Mark the timeblock as blocked
			break; // No need to check further limits for this timeblock; it has already failed.
		  } else {
			// This specific limit check passed
			logDebug(`      OK: Limit for >=${limitSize} not exceeded. (Existing: ${existingMatchingGroupsCount} + New: 1 <= Max: ${maxAllowedCount})`);
		  }
		} else {
		  // The new booking's guest count is smaller than the current limit threshold, so this rule doesn't restrict it.
		  logDebug(`      Not Applicable: Booking guests (${guestsNumber}) < Limit threshold (${limitSize}). Skipping this check.`);
		}
	  } // End of loop through different limitSizes (e.g., checking 6, then 7)
  
	  // After checking all applicable limits for the timeblock:
	  if (isTimeAllowed) {
		logDebug(`  ✅ Result for time ${time}: ALLOWED (passed all applicable checks)`);
		filteredBlocks[time] = timeData; // Add it to the results
	  } else {
		  logDebug(`  ❌ Result for time ${time}: BLOCKED (failed at least one limit check)`);
		  // Do not add it to filteredBlocks
	  }
	} // End of loop through all input timeblocks
  
	logDebug(`--- MaxGroupsFilter Finished. Output timeblocks count: ${Object.keys(filteredBlocks).length} ---`);
	return filteredBlocks; // Return the timeblocks that passed all checks
  }
  
  // Export the function for use in other parts of the application
  module.exports = {
	filterTimeblocksByMaxGroups,
	getMealType, // Export getMealType for potential use by other modules
  };